/**
 * Auto-rendered editor for scheduled-task `outputs[]` field.
 *
 * Fetches handler descriptors from
 *   GET /v2/admin/scheduled-tasks/output-types
 * and renders a config form for each output entry using the JSON Schema
 * returned by the handler. Adding a new output type on the backend
 * (one-file plugin) needs ZERO edits here — the schema drives the UI.
 */

import { useEffect, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import Textarea from './shared/Textarea';

function _isEnum(schema) {
    return Array.isArray(schema?.enum) && schema.enum.length > 0;
}

function _isArrayOfEnum(schema) {
    return schema?.type === 'array' && _isEnum(schema.items || {});
}

function _isKeyValueObject(schema) {
    return (
        schema?.type === 'object' &&
        (schema.additionalProperties === true ||
            (typeof schema.additionalProperties === 'object' && schema.additionalProperties !== null))
    );
}

function _defaultFor(schema) {
    if (schema?.default !== undefined) return schema.default;
    if (_isEnum(schema)) return schema.enum[0];
    if (schema?.type === 'integer' || schema?.type === 'number') return '';
    if (schema?.type === 'boolean') return false;
    if (schema?.type === 'array') return [];
    if (schema?.type === 'object') return {};
    return '';
}

function _fieldDefaults(configSchema) {
    const out = {};
    const props = configSchema?.properties || {};
    for (const key of Object.keys(props)) {
        const s = props[key];
        if (s.default !== undefined) out[key] = s.default;
    }
    return out;
}

const VARIABLE_SUGGESTIONS = [
    { label: 'Từ kết quả structured', items: [
        { insert: '{{structured.canonical_id}}', hint: 'Mã cổ phiếu từ JSON output' },
        { insert: '{{structured.recommendation}}', hint: 'Khuyến nghị (BUY/HOLD/SELL)' },
        { insert: '{{structured.title}}', hint: 'Tiêu đề từ structured output' },
    ]},
    { label: 'Từ biến chạy (variables)', items: [
        { insert: '{{var.symbol}}', hint: 'Biến symbol do caller truyền vào' },
        { insert: '{{var.custom}}', hint: 'Biến tuỳ chỉnh' },
    ]},
    { label: 'Từ task / run', items: [
        { insert: '{{task.id}}', hint: 'Mã tác vụ' },
        { insert: '{{task.name}}', hint: 'Tên tác vụ' },
        { insert: '{{run.id}}', hint: 'Mã lượt chạy' },
        { insert: '{{now}}', hint: 'Thời gian chạy (ISO)' },
    ]},
    { label: 'Mẫu có fallback', items: [
        { insert: '{{structured.canonical_id | var.symbol | "equity:HOSE:VNM"}}', hint: 'Chuỗi ưu tiên + giá trị mặc định' },
    ]},
];

const RESOLVER_SAMPLE_DEFAULTS = {
    'structured.canonical_id': 'equity:HOSE:VNM',
    'structured.ticker': 'VNM',
    'structured.severity': 'high',
    'structured.cta': 'Mua vào',
    'structured.content': 'Nội dung báo cáo mẫu...',
    'structured.summary': 'Tóm tắt ngắn gọn',
    'structured.headline': 'Headline mẫu',
    'structured.title': 'Tiêu đề mẫu',
    'structured.tickers': ['VNM', 'MSN'],
    'var.symbol': 'VNM',
    'var.canonical_id': 'equity:HOSE:VNM',
    'task.id': 'preview',
    'task.name': 'Tác vụ mẫu',
    'run.id': 0,
    now: '2026-04-22T00:00:00Z',
    today: '2026-04-22',
};

const CLIENT_PLACEHOLDER_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

function _lookupPath(path, ctx) {
    const parts = path.split('.').map((s) => s.trim()).filter(Boolean);
    let cur = ctx;
    for (const k of parts) {
        if (cur == null) return undefined;
        cur = cur[k];
    }
    return cur;
}

function resolveTemplateSample(template, extraContext = {}) {
    if (!template || typeof template !== 'string' || !template.includes('{{')) return template;
    const ctx = {
        structured: {}, var: {}, task: {}, run: {}, user: {}, now: '', today: '', text: '',
    };
    for (const [path, v] of Object.entries(RESOLVER_SAMPLE_DEFAULTS)) {
        const parts = path.split('.');
        if (parts.length === 1) { ctx[parts[0]] = v; continue; }
        let cur = ctx;
        for (let i = 0; i < parts.length - 1; i++) {
            if (typeof cur[parts[i]] !== 'object' || cur[parts[i]] === null) cur[parts[i]] = {};
            cur = cur[parts[i]];
        }
        cur[parts[parts.length - 1]] = v;
    }
    for (const [k, v] of Object.entries(extraContext || {})) {
        if (!(k in ctx)) ctx[k] = v;
    }
    return template.replace(CLIENT_PLACEHOLDER_RE, (_, raw) => {
        const expr = raw.trim();
        if (expr.startsWith('#') || expr.startsWith('/')) return _;
        const alts = expr.split('|').map((s) => s.trim()).filter(Boolean);
        for (const alt of alts) {
            const literal = /^"(.*)"$|^'(.*)'$/.exec(alt);
            if (literal) return literal[1] ?? literal[2] ?? '';
            const val = _lookupPath(alt, ctx);
            if (val !== undefined && val !== null && val !== '') {
                return typeof val === 'object' ? JSON.stringify(val) : String(val);
            }
        }
        return '';
    });
}

function SamplePreviewButton({ value }) {
    const [open, setOpen] = useState(false);
    if (!value || typeof value !== 'string' || !value.includes('{{')) return null;
    const resolved = resolveTemplateSample(value);
    return (
        <div className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200"
                title="Xem giá trị mẫu sau khi resolve biến"
            >
                👁 Mẫu
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 text-left">
                        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Template</div>
                        <div className="text-xs font-mono text-blue-700 break-all mb-2">{value}</div>
                        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Giá trị mẫu</div>
                        <div className="text-xs font-mono text-emerald-700 break-all">{resolved || <span className="text-gray-400 italic">(rỗng)</span>}</div>
                    </div>
                </>
            )}
        </div>
    );
}

function FieldLabel({ name, schema, value, required, onInsert }) {
    const label = schema?.title || name;
    const isTemplate = !!schema?.['x-template'];
    return (
        <div className="flex items-center justify-between mb-1 gap-2">
            <div className="flex items-center gap-1 min-w-0">
                <label className="text-sm font-medium text-gray-700 truncate" title={label}>
                    {label}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {isTemplate && (
                    <span className="inline-flex items-center shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Trường này hỗ trợ biến {{...}}">
                        Biến
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {isTemplate && <SamplePreviewButton value={value} />}
                {isTemplate && onInsert && <VariablePicker onInsert={onInsert} />}
                <span className="text-[11px] text-gray-400 font-mono" title={`Khoá API: ${name}`}>
                    {name}
                </span>
            </div>
        </div>
    );
}

function FieldHelp({ schema }) {
    if (!schema?.description) return null;
    return (
        <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{schema.description}</p>
    );
}

function VariablePicker({ onInsert }) {
    const [open, setOpen] = useState(false);
    const handleSelect = (insert) => {
        onInsert(insert);
        setOpen(false);
    };
    return (
        <div className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200"
                title="Chèn biến runtime"
            >
                + Biến
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                        {VARIABLE_SUGGESTIONS.map((group) => (
                            <div key={group.label} className="py-1">
                                <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                    {group.label}
                                </div>
                                {group.items.map((item) => (
                                    <button
                                        key={item.insert}
                                        type="button"
                                        onClick={() => handleSelect(item.insert)}
                                        className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-t border-gray-50 first:border-t-0"
                                    >
                                        <div className="text-xs font-mono text-blue-700 break-all">{item.insert}</div>
                                        <div className="text-[10px] text-gray-500">{item.hint}</div>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function JsonEditorField({ name, schema, value, onChange, required, onLoadPreset }) {
    const [raw, setRaw] = useState(() =>
        value && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '{}')
    );
    const [parseError, setParseError] = useState(null);

    useEffect(() => {
        const next = value && typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '{}');
        try {
            const mine = JSON.parse(raw || '{}');
            if (JSON.stringify(mine) !== JSON.stringify(value || {})) setRaw(next);
        } catch {
            setRaw(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(value)]);

    const handleChange = (text) => {
        setRaw(text || '');
        try {
            const parsed = JSON.parse(text || '{}');
            if (parsed && typeof parsed === 'object') {
                setParseError(null);
                onChange(parsed);
            } else {
                setParseError('Phải là JSON object');
            }
        } catch (e) {
            setParseError('JSON không hợp lệ: ' + e.message);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                    {schema?.title || name}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <div className="flex items-center gap-2">
                    {onLoadPreset && (
                        <Button variant="ghost" size="xs" onClick={onLoadPreset}>
                            Tải preset
                        </Button>
                    )}
                    {parseError ? (
                        <span className="text-[11px] text-red-500">{parseError}</span>
                    ) : (
                        <span className="text-[11px] text-green-600">JSON hợp lệ</span>
                    )}
                </div>
            </div>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
                <Editor
                    height="280px"
                    language="json"
                    theme="vs"
                    value={raw}
                    onChange={(v) => handleChange(v ?? '')}
                    options={{
                        minimap: { enabled: false },
                        fontSize: 12,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        formatOnPaste: true,
                        automaticLayout: true,
                        tabSize: 2,
                    }}
                />
            </div>
            <FieldHelp schema={schema} />
        </div>
    );
}


function SchemaField({ name, schema, value, onChange, required, onLoadPreset }) {
    if (schema?.['x-editor'] === 'json') {
        return (
            <JsonEditorField
                name={name}
                schema={schema}
                value={value}
                onChange={onChange}
                required={required}
                onLoadPreset={onLoadPreset}
            />
        );
    }
    if (_isEnum(schema)) {
        const labels = schema['x-enum-labels'] || {};
        return (
            <div>
                <FieldLabel name={name} schema={schema} required={required} />
                <Select
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    options={schema.enum.map((v) => ({ value: v, label: labels[v] || String(v) }))}
                />
                <FieldHelp schema={schema} />
            </div>
        );
    }

    if (_isArrayOfEnum(schema)) {
        const values = Array.isArray(value) ? value : [];
        const options = schema.items.enum;
        return (
            <div>
                <FieldLabel name={name} schema={schema} required={required} />
                <div className="flex flex-wrap gap-2">
                    {options.map((opt) => {
                        const checked = values.includes(opt);
                        return (
                            <label
                                key={opt}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border cursor-pointer text-xs ${
                                    checked ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={checked}
                                    onChange={() => {
                                        const next = checked ? values.filter((v) => v !== opt) : [...values, opt];
                                        onChange(next);
                                    }}
                                />
                                {opt}
                            </label>
                        );
                    })}
                </div>
                <FieldHelp schema={schema} />
            </div>
        );
    }

    if (schema.type === 'integer' || schema.type === 'number') {
        return (
            <div>
                <FieldLabel name={name} schema={schema} required={required} />
                <Input
                    type="number"
                    value={value === undefined || value === null ? '' : value}
                    min={schema.minimum}
                    max={schema.maximum}
                    placeholder={schema.default !== undefined ? String(schema.default) : ''}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') return onChange(undefined);
                        const parsed = schema.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
                        onChange(Number.isNaN(parsed) ? undefined : parsed);
                    }}
                />
                <FieldHelp schema={schema} />
            </div>
        );
    }

    if (schema.type === 'boolean') {
        const label = schema?.title || name;
        return (
            <div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 py-1">
                    <input
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                    {label}
                </label>
                <FieldHelp schema={schema} />
            </div>
        );
    }

    if (_isKeyValueObject(schema)) {
        return <KeyValueField name={name} schema={schema} value={value} onChange={onChange} required={required} />;
    }

    const isMultiline = typeof value === 'string' && (value.includes('\n') || (value?.length || 0) > 80);
    const insertVariable = (snippet) => {
        const current = value ?? '';
        const next = current ? `${current}${current.endsWith(' ') ? '' : ' '}${snippet}` : snippet;
        onChange(next);
    };
    return (
        <div>
            <FieldLabel name={name} schema={schema} value={value} required={required} onInsert={insertVariable} />
            {isMultiline ? (
                <Textarea
                    rows={4}
                    value={value ?? ''}
                    placeholder={schema.default ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            ) : (
                <Input
                    value={value ?? ''}
                    placeholder={schema.default ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
            <FieldHelp schema={schema} />
        </div>
    );
}

function KeyValueField({ name, schema, value, onChange, required }) {
    const entries = useMemo(() => {
        if (!value || typeof value !== 'object') return [];
        return Object.entries(value);
    }, [value]);
    const [draftKey, setDraftKey] = useState('');
    const [draftVal, setDraftVal] = useState('');

    const setKey = (oldKey, newKey) => {
        if (!newKey || newKey === oldKey) return;
        const next = { ...(value || {}) };
        next[newKey] = next[oldKey];
        delete next[oldKey];
        onChange(next);
    };
    const setVal = (k, v) => onChange({ ...(value || {}), [k]: v });
    const remove = (k) => {
        const next = { ...(value || {}) };
        delete next[k];
        onChange(next);
    };
    const addEntry = () => {
        if (!draftKey) return;
        onChange({ ...(value || {}), [draftKey]: draftVal });
        setDraftKey('');
        setDraftVal('');
    };

    return (
        <div>
            <FieldLabel name={name} schema={schema} required={required} />
            <div className="space-y-1">
                {entries.map(([k, v]) => (
                    <div key={k} className="flex gap-1">
                        <Input value={k} onChange={(e) => setKey(k, e.target.value)} className="flex-1" />
                        <Input
                            value={typeof v === 'string' ? v : JSON.stringify(v)}
                            onChange={(e) => setVal(k, e.target.value)}
                            className="flex-[2]"
                        />
                        <Button variant="ghost" size="xs" onClick={() => remove(k)}>
                            ✕
                        </Button>
                    </div>
                ))}
                <div className="flex gap-1 pt-1">
                    <Input placeholder="khoá" value={draftKey} onChange={(e) => setDraftKey(e.target.value)} className="flex-1" />
                    <Input placeholder="giá trị" value={draftVal} onChange={(e) => setDraftVal(e.target.value)} className="flex-[2]" />
                    <Button variant="ghost" size="xs" onClick={addEntry}>
                        +
                    </Button>
                </div>
            </div>
            <FieldHelp schema={schema} />
        </div>
    );
}

function OutputConfigForm({ schema, config, onChange, fieldActions }) {
    const props = schema?.properties || {};
    const required = new Set(schema?.required || []);
    const keys = Object.keys(props);
    return (
        <div className="space-y-3">
            {keys.map((key) => (
                <SchemaField
                    key={key}
                    name={key}
                    schema={props[key]}
                    value={config?.[key]}
                    required={required.has(key)}
                    onLoadPreset={fieldActions?.[key]?.onLoadPreset}
                    onChange={(val) => {
                        const next = { ...(config || {}) };
                        if (val === undefined || val === '') delete next[key];
                        else next[key] = val;
                        onChange(next);
                    }}
                />
            ))}
        </div>
    );
}




function WebhookPreviewButton({ config }) {
    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [sample, setSample] = useState(() => JSON.stringify({
        structured: {
            canonical_id: 'equity:HOSE:VNM',
            ticker: 'VNM',
            severity: 'important',
            event_type: 'Test',
            summary: 'VNM tăng +2.1% sau tin tức',
            content: '**Chi tiết**\n- Giá: 72.5k\n- +2.1%',
            tickers: 'VNM',
            cta: {
                cta_intro: 'Hành động gợi ý:',
                cta_items: [
                    { label: 'Xem chart', description: 'Biểu đồ', prompt: 'vẽ chart' },
                ],
            },
        },
        var: {},
        task: { id: 'preview', name: 'Preview', agent_id: 'simplize' },
        run: { id: 0 },
    }, null, 2));

    const run = async () => {
        setError(null);
        setResult(null);
        let sample_context;
        try {
            sample_context = JSON.parse(sample);
        } catch (e) {
            setError('JSON ngữ cảnh không hợp lệ: ' + e.message);
            return;
        }
        setRunning(true);
        try {
            const resp = await adminApi.previewWebhookBody({ config, sample_context });
            setResult(resp);
        } catch (e) {
            setError(e?.message || 'Không gọi được API preview');
        } finally {
            setRunning(false);
        }
    };

    return (
        <>
            <Button variant="ghost" size="xs" onClick={(e) => { e.stopPropagation(); setOpen(true); run(); }}>
                Xem trước payload
            </Button>
            {open && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">Xem trước payload webhook</h3>
                                <div className="text-xs text-gray-500 mt-0.5">
                                    format=<code>{config?.format || 'raw'}</code>
                                    {config?.url && <span className="ml-2">→ {config.url.slice(0, 60)}{config.url.length > 60 ? '…' : ''}</span>}
                                </div>
                            </div>
                            <button onClick={() => setOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                        </div>
                        <div className="flex-1 grid grid-cols-2 divide-x divide-gray-100 overflow-hidden">
                            <div className="flex flex-col">
                                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600 uppercase">Ngữ cảnh mẫu</span>
                                    <Button variant="primary" size="xs" onClick={run} loading={running}>Chạy lại</Button>
                                </div>
                                <textarea
                                    className="flex-1 p-4 font-mono text-xs resize-none focus:outline-none"
                                    value={sample}
                                    onChange={(e) => setSample(e.target.value)}
                                    spellCheck={false}
                                />
                            </div>
                            <div className="flex flex-col overflow-hidden">
                                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                    <span className="text-xs font-medium text-gray-600 uppercase">Body sẽ POST</span>
                                </div>
                                <div className="flex-1 overflow-auto p-4 text-xs">
                                    {error && <div className="text-red-600">{error}</div>}
                                    {result && (
                                        <pre className="whitespace-pre-wrap break-words">
                                            {JSON.stringify(result.body, null, 2)}
                                        </pre>
                                    )}
                                    {!running && !result && !error && (
                                        <div className="text-gray-400">Nhấn "Chạy lại" để xem kết quả.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function _summarizeConfig(config, schema) {
    if (!config || typeof config !== 'object') return '';
    const props = schema?.properties || {};
    const keys = Object.keys(props);
    const hint = [];
    for (const key of keys) {
        const v = config[key];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
        if (typeof v === 'object') {
            if (Array.isArray(v)) {
                hint.push(`${key}=[${v.join(',')}]`);
            } else {
                const count = Object.keys(v).length;
                if (count > 0) hint.push(`${key}(${count})`);
            }
        } else {
            const str = String(v);
            hint.push(`${key}=${str.length > 24 ? str.slice(0, 24) + '…' : str}`);
        }
        if (hint.length >= 3) break;
    }
    return hint.join(' · ');
}

export default function TaskOutputsEditor({ value, onChange }) {
    const outputs = Array.isArray(value) ? value : [];
    const [types, setTypes] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [t, p] = await Promise.all([
                    adminApi.listScheduledTaskOutputTypes(),
                    adminApi.listWebhookPresets().catch(() => []),
                ]);
                if (!cancelled) {
                    setTypes(t || []);
                    setPresets(Array.isArray(p) ? p : []);
                }
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Không tải được danh sách đầu ra');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const typeIndex = useMemo(() => {
        const m = {};
        for (const t of types) m[t.type] = t;
        return m;
    }, [types]);

    const presetIndex = useMemo(() => {
        const m = {};
        for (const p of presets) m[p.format] = p;
        return m;
    }, [presets]);

    const updateOutput = (idx, next) => {
        const copy = outputs.slice();
        copy[idx] = next;
        onChange(copy);
    };
    const removeOutput = (idx) => {
        onChange(outputs.filter((_, i) => i !== idx));
        setExpanded((prev) => {
            const next = {};
            for (const k of Object.keys(prev)) {
                const n = Number(k);
                if (n < idx) next[n] = prev[k];
                else if (n > idx) next[n - 1] = prev[k];
            }
            return next;
        });
    };
    const addOutput = (type) => {
        const desc = typeIndex[type];
        const defaults = desc ? _fieldDefaults(desc.config_schema) : {};
        const newIdx = outputs.length;
        onChange([...outputs, { type, config: defaults }]);
        setExpanded((prev) => ({ ...prev, [newIdx]: true }));
    };
    const toggle = (idx) =>
        setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

    if (loading) return <div className="text-xs text-gray-400">Đang tải danh sách đầu ra…</div>;
    if (error) return <div className="text-xs text-red-500">Lỗi: {error}</div>;
    if (types.length === 0) return <div className="text-xs text-gray-400">Server chưa đăng ký handler đầu ra nào.</div>;

    return (
        <div className="space-y-3">
            {outputs.map((out, idx) => {
                const desc = typeIndex[out.type];
                const isOpen = !!expanded[idx];
                const summary = desc ? _summarizeConfig(out.config || {}, desc.config_schema) : '';
                return (
                    <div key={idx} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                        <div
                            className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50"
                            onClick={() => toggle(idx)}
                        >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-gray-400 text-xs shrink-0">{isOpen ? '▾' : '▸'}</span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-800 truncate">
                                        {desc?.display_name || out.type}
                                    </div>
                                    {!isOpen && summary && (
                                        <div className="text-[11px] text-gray-500 truncate font-mono">{summary}</div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                {out.type === 'webhook' && (
                                    <WebhookPreviewButton config={out.config || {}} />
                                )}
                                <Button
                                    variant="ghost" size="xs" className="text-red-500"
                                    onClick={(e) => { e.stopPropagation(); removeOutput(idx); }}
                                >
                                    Xoá
                                </Button>
                            </div>
                        </div>
                        {isOpen && (
                            <div className="px-3 pb-3 pt-1 border-t border-gray-100">
                                {desc?.description && (
                                    <div className="text-[11px] text-gray-500 mb-3">{desc.description}</div>
                                )}
                                {desc ? (
                                    <OutputConfigForm
                                        schema={desc.config_schema}
                                        config={out.config || {}}
                                        onChange={(cfg) => updateOutput(idx, { ...out, config: cfg })}
                                        fieldActions={out.type === 'webhook' ? {
                                            body_template: {
                                                onLoadPreset: () => {
                                                    const fmt = out.config?.format || 'raw';
                                                    const preset = presetIndex[fmt];
                                                    if (!preset) {
                                                        alert(`Không tìm thấy preset cho format "${fmt}".`);
                                                        return;
                                                    }
                                                    const hasContent = out.config?.body_template && Object.keys(out.config.body_template).length > 0;
                                                    if (hasContent && !confirm(`Thay thế body_template hiện tại bằng preset "${preset.display_name}"?`)) return;
                                                    updateOutput(idx, {
                                                        ...out,
                                                        config: { ...(out.config || {}), body_template: JSON.parse(JSON.stringify(preset.body_template)) },
                                                    });
                                                },
                                            },
                                        } : undefined}
                                    />
                                ) : (
                                    <div className="text-xs text-red-500">
                                        Loại '{out.type}' chưa được đăng ký trên server.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="flex gap-2 flex-wrap">
                {types.map((t) => (
                    <Button key={t.type} variant="secondary" size="sm" onClick={() => addOutput(t.type)}>
                        + {t.display_name}
                    </Button>
                ))}
            </div>
        </div>
    );
}
